import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import CategoryManager from "../components/CategoryManager";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Shield,
  FileText,
  Upload,
  Download,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback categories if none exist in database
const DEFAULT_SAFETY_CATEGORIES = [
  { value: "general", label: "General Safety" },
  { value: "highrise", label: "Highrise Work" },
  { value: "lowrise", label: "Lowrise Work" },
  { value: "fall_protection", label: "Fall Protection" },
  { value: "whmis", label: "WHMIS" },
  { value: "ppe", label: "PPE Requirements" },
  { value: "equipment", label: "Equipment Safety" },
  { value: "emergency", label: "Emergency Procedures" },
];

const SafetyDocuments = () => {
  const { isOfficeManager } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [services, setServices] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    document_type: "template",
    category: "general",
    content: "",
    requires_acknowledgment: true,
    service_ids: [],
    address_ids: [],
  });

  const fetchData = useCallback(async () => {
    try {
      const [docsRes, servicesRes, addressesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/safety-documents`),
        axios.get(`${API}/services`),
        axios.get(`${API}/company-addresses`).catch(() => ({ data: [] })),
        axios.get(`${API}/categories/?category_type=safety`).catch(() => ({ data: [] })),
      ]);
      setDocuments(docsRes.data);
      setServices(servicesRes.data);
      setAddresses(addressesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: "",
      document_type: "template",
      category: "general",
      content: "",
      requires_acknowledgment: true,
      service_ids: [],
      address_ids: [],
    });
    setEditingDoc(null);
  };

  const handleOpenDialog = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        name: doc.name,
        document_type: doc.document_type,
        category: doc.category,
        content: doc.content || "",
        requires_acknowledgment: doc.requires_acknowledgment,
        service_ids: doc.service_ids || [],
        address_ids: doc.address_ids || [],
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingDoc) {
        await axios.put(`${API}/safety-documents/${editingDoc.id}`, formData);
        toast.success("Safety document updated");
      } else {
        await axios.post(`${API}/safety-documents`, formData);
        toast.success("Safety document created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Archive this safety document?")) return;
    try {
      await axios.delete(`${API}/safety-documents/${id}`);
      toast.success("Safety document archived");
      fetchData();
    } catch (error) {
      toast.error("Failed to archive document");
    }
  };

  const handleUploadFile = async (file) => {
    if (!selectedDocId) return;
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/safety-documents/${selectedDocId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      setUploadDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    }
  };

  const handleDownload = async (doc) => {
    if (!doc.file_path) {
      toast.error("No file attached to this document");
      return;
    }
    window.open(`${API}/safety-documents/${doc.id}/download`, "_blank");
  };

  const toggleServiceAssociation = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      service_ids: prev.service_ids.includes(serviceId)
        ? prev.service_ids.filter((id) => id !== serviceId)
        : [...prev.service_ids, serviceId],
    }));
  };

  const toggleAddressAssociation = (addressId) => {
    setFormData((prev) => ({
      ...prev,
      address_ids: prev.address_ids.includes(addressId)
        ? prev.address_ids.filter((id) => id !== addressId)
        : [...prev.address_ids, addressId],
    }));
  };

  const filteredDocs = documents.filter((doc) => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || doc.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="safety-documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-8 w-8 text-primary" />
            Safety Documents
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage safety sheets and documentation for jobs
          </p>
        </div>
        {isOfficeManager && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-safety-doc-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Document
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <CategoryManager
              categoryType="safety"
              label="All Categories"
              onCategoriesChange={fetchData}
              showAsDropdown={true}
              selectedValue={filterCategory}
              onSelectionChange={(v) => setFilterCategory(v === "all" ? "" : v)}
              allowAll={true}
            />
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="hidden md:table-cell">Acknowledgment</TableHead>
                <TableHead className="hidden lg:table-cell">Associations</TableHead>
                <TableHead className="hidden md:table-cell">File</TableHead>
                {isOfficeManager && <TableHead className="w-32">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No safety documents found
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocs.map((doc) => (
                  <TableRow key={doc.id} data-testid={`safety-doc-${doc.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        {doc.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {categories.find((c) => c.name?.toLowerCase().replace(/\s+/g, '_') === doc.category)?.name || 
                         DEFAULT_SAFETY_CATEGORIES.find((c) => c.value === doc.category)?.label || 
                         doc.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={doc.document_type === "template" ? "default" : "secondary"}>
                        {doc.document_type === "template" ? "Template" : "Job-Specific"}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {doc.requires_acknowledgment ? (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Required
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Optional
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="text-sm text-muted-foreground">
                        {doc.service_ids?.length || 0} services, {doc.address_ids?.length || 0} addresses
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {doc.file_path ? (
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                          <Download className="h-4 w-4" />
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    {isOfficeManager && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(doc)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedDocId(doc.id);
                              setUploadDialogOpen(true);
                            }}
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(doc.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDoc ? "Edit Safety Document" : "Create Safety Document"}</DialogTitle>
            <DialogDescription>
              {editingDoc ? "Update the safety document details." : "Create a new safety document or template."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Document Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Highrise Fall Protection"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.document_type}
                  onValueChange={(v) => setFormData({ ...formData, document_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="template">Template (Reusable)</SelectItem>
                    <SelectItem value="job_specific">Job-Specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <CategoryManager
                categoryType="safety"
                label="Category"
                onCategoriesChange={fetchData}
                showAsDropdown={true}
                selectedValue={formData.category}
                onSelectionChange={(v) => setFormData({ ...formData, category: v })}
                allowAll={false}
              />
            </div>

            <div className="space-y-2">
              <Label>Content (Markdown supported)</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={8}
                placeholder="Enter safety document content..."
              />
            </div>

            <div className="flex items-center gap-2 p-3 border rounded-lg">
              <Switch
                id="requires_ack"
                checked={formData.requires_acknowledgment}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, requires_acknowledgment: checked })
                }
              />
              <Label htmlFor="requires_ack" className="cursor-pointer">
                Require employee acknowledgment
              </Label>
            </div>

            {/* Service Associations */}
            {services.length > 0 && (
              <div className="space-y-2">
                <Label>Associate with Services</Label>
                <div className="border rounded-lg p-3 max-h-32 overflow-y-auto space-y-2">
                  {services.map((service) => (
                    <div key={service.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`service-${service.id}`}
                        checked={formData.service_ids.includes(service.id)}
                        onCheckedChange={() => toggleServiceAssociation(service.id)}
                      />
                      <Label htmlFor={`service-${service.id}`} className="text-sm cursor-pointer">
                        {service.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Address Associations */}
            {addresses.length > 0 && (
              <div className="space-y-2">
                <Label>Associate with Job Addresses</Label>
                <div className="border rounded-lg p-3 max-h-32 overflow-y-auto space-y-2">
                  {addresses.map((addr) => (
                    <div key={addr.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`addr-${addr.id}`}
                        checked={formData.address_ids.includes(addr.id)}
                        onCheckedChange={() => toggleAddressAssociation(addr.id)}
                      />
                      <Label htmlFor={`addr-${addr.id}`} className="text-sm cursor-pointer">
                        {addr.building_name || addr.street_address}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingDoc ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload PDF File</DialogTitle>
            <DialogDescription>Attach a PDF file to this safety document.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="file"
              accept=".pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadFile(file);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyDocuments;
